import mixpanel from 'mixpanel-browser';
import type PptxGenJS from 'pptxgenjs';

import { FeatureTrackingEvents } from '@revelio/core';

import {
  planReportSlides,
  REPORT_SLIDE,
  REPORT_SLIDE_TABLE,
  REPORT_SLIDE_TEXT,
  type SlideChartElement,
  type SlidePlan,
} from '../../../../ReportFlow/ppt/report-flow-slide-plan';
import type {
  ReportFlowDocument,
  ReportFlowLeafBlock,
} from '../../../../ReportFlow/report-flow-types';
import type { ParameterSelection } from '../../../utils/check-datapoint-in-parameter';
import type { ChartExportInfo } from '../../chart-export-types';
import {
  type CapturedChartImage,
  captureChartImage,
  fitImageToBox,
  isImageOnlyChartType,
} from './ppt-chart-image';
import { createPptx, deckFileName, getReplotChartType } from './ppt-exporter';
import { addChartToReportSlide } from './ppt-report-exporter';
import { addMarkdownSegmentsToSlide } from './ppt-slide-generator';
import {
  defineWideReportSlideMasters,
  pptTheme,
  REPORT_WIDE_CONTENT_MASTER,
  REPORT_WIDE_LAYOUT,
  reportWidePptTheme,
} from './ppt-theme';
import {
  addWideClosingSlide,
  addWideCoverSlide,
  darkPageBackground,
} from './ppt-wide-report-bookends';

const PX_PER_INCH = 96;
const CONTINUED_SUFFIX = ' (cont.)';

export type ChartImageRequest = {
  itemId: string;
  leaf: ReportFlowLeafBlock;
  widthPx: number;
  heightPx: number;
};

export type ChartImageRenderer = (
  requests: ChartImageRequest[]
) => Promise<Map<string, CapturedChartImage>>;

export type FlowReportExportParams = {
  doc: ReportFlowDocument;
  charts: ChartExportInfo[];
  reportTitle: string;
  reportDescription?: string;
  coverParameters?: { label: string; value: string }[];
  downloadName?: string;
  renderChartImages?: ChartImageRenderer | null;
  primarySelections?: ParameterSelection[];
};

// Native charts by default; only sankey/chord/treemap/icicle, image items and unregistered charts become pictures.
const needsImage = (
  element: SlideChartElement,
  chart: ChartExportInfo | undefined
) =>
  element.leaf.kind === 'image' ||
  chart === undefined ||
  isImageOnlyChartType(getReplotChartType(chart));

const textByItemId = (charts: ChartExportInfo[]) =>
  new Map(
    charts.flatMap((chart) =>
      chart.itemId && chart.dataType === 'text' && chart.textContent
        ? [[chart.itemId, chart.textContent] as const]
        : []
    )
  );

const imageRequests = (
  plan: SlidePlan[],
  chartById: Map<string, ChartExportInfo>
): ChartImageRequest[] =>
  plan.flatMap((slide) =>
    slide.elements
      .filter(
        (element): element is SlideChartElement =>
          element.kind === 'chart' &&
          needsImage(element, chartById.get(element.itemId))
      )
      .map((element) => ({
        itemId: element.itemId,
        leaf: element.leaf,
        widthPx: Math.round(element.box.w * PX_PER_INCH),
        heightPx: Math.round(element.box.h * PX_PER_INCH),
      }))
  );

const addSlideTitle = (slide: PptxGenJS.Slide, plan: SlidePlan) => {
  if (!plan.title) return;
  slide.addText(`${plan.title}${plan.continued ? CONTINUED_SUFFIX : ''}`, {
    x: REPORT_SLIDE.marginXIn,
    y: REPORT_SLIDE.titleYIn,
    w: REPORT_SLIDE.widthIn - 2 * REPORT_SLIDE.marginXIn,
    h: REPORT_SLIDE.titleHIn,
    fontFace: pptTheme.fonts.face,
    fontSize: reportWidePptTheme.fonts.slideTitleSize,
    color: pptTheme.colors.text.replace('#', ''),
    valign: 'middle',
    margin: 0,
    fit: 'shrink',
  });
};

const addChartElement = async (
  slide: PptxGenJS.Slide,
  element: SlideChartElement,
  chart: ChartExportInfo | undefined,
  image: CapturedChartImage | undefined,
  primarySelections: ParameterSelection[] | undefined
) => {
  if (image) {
    slide.addImage({ data: image.dataUrl, ...element.box });
    return;
  }
  if (!chart) return;
  if (!needsImage(element, chart)) {
    await addChartToReportSlide(slide, chart, element.box, {
      title: element.leaf.item.title?.trim() ? chart.title : '',
      highlight: primarySelections,
      sort: element.leaf.item.plot?.sort,
    });
    return;
  }
  // The slide-size render failed: fall back to the live card, never to a bar chart.
  const live = await captureChartImage(chart.captureRef?.current);
  if (!live) {
    console.warn(`No rendered chart to capture for slide: ${chart.title}`);
    return;
  }
  slide.addImage({ data: live.dataUrl, ...fitImageToBox(live, element.box) });
};

const createFlowReportPptxDoc = async (params: FlowReportExportParams) => {
  const chartById = new Map(
    params.charts.flatMap((chart) =>
      chart.itemId ? [[chart.itemId, chart] as const] : []
    )
  );
  const plan = planReportSlides(params.doc, textByItemId(params.charts));
  const requests = imageRequests(plan, chartById);
  const images =
    params.renderChartImages && requests.length > 0
      ? await params.renderChartImages(requests).catch((error: unknown) => {
          console.warn('Chart rendering for PPT failed:', error);
          return new Map<string, CapturedChartImage>();
        })
      : new Map<string, CapturedChartImage>();

  const pptx = await createPptx();
  pptx.layout = REPORT_WIDE_LAYOUT;
  const dateLabel = new Date().toLocaleDateString('en-US');
  defineWideReportSlideMasters(pptx, {
    footerLabel: `${params.reportTitle}  |  ${dateLabel}`,
    darkBackground: darkPageBackground(),
  });

  addWideCoverSlide(pptx, {
    title: params.reportTitle,
    description: params.reportDescription,
    parameters: [
      ...(params.coverParameters ?? []),
      { label: 'Date', value: dateLabel },
    ],
  });

  for (const slidePlan of plan) {
    const slide = pptx.addSlide({ masterName: REPORT_WIDE_CONTENT_MASTER });
    addSlideTitle(slide, slidePlan);
    for (const element of slidePlan.elements) {
      if (element.kind === 'text') {
        await addMarkdownSegmentsToSlide(slide, element.markdown, element.box, {
          layout: 'stacked',
          bodyPt: REPORT_SLIDE_TEXT.bodyPt,
          headingSizes: REPORT_SLIDE_TEXT.headingPt,
          paragraphGapPt: REPORT_SLIDE_TEXT.paragraphGapPt,
          tablePt: 10,
          tableRowHIn: REPORT_SLIDE_TABLE.rowHIn,
          tableGapIn: REPORT_SLIDE_TABLE.gapIn,
          borderColor: reportWidePptTheme.colors.rule,
        });
      } else {
        await addChartElement(
          slide,
          element,
          chartById.get(element.itemId),
          images.get(element.itemId),
          params.primarySelections
        );
      }
    }
  }

  await addWideClosingSlide(pptx);

  const fileName = deckFileName(
    params.reportTitle,
    undefined,
    params.downloadName
  );
  return { pptx, fileName };
};

export const exportFlowReportToPpt = (params: FlowReportExportParams) => ({
  async getFile() {
    const { fileName, pptx } = await createFlowReportPptxDoc(params);
    mixpanel.track(FeatureTrackingEvents.PPT_DOWNLOAD, {
      plot_title: params.reportTitle,
      scope: 'report',
    });
    await pptx.writeFile({ fileName });
  },
  async getBlob() {
    const { fileName, pptx } = await createFlowReportPptxDoc(params);
    const blob = (await pptx.write({ outputType: 'blob' })) as Blob;
    mixpanel.track(FeatureTrackingEvents.PPT_PREVIEW, {
      plot_title: params.reportTitle,
      scope: 'report',
    });
    return { blob, fileName };
  },
});
